import EditIcon from '@mui/icons-material/Edit';
import { Box, CircularProgress, SxProps, TextField, Theme, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

/**
 * Props for the EditableBuildingName component
 */
interface EditableBuildingNameProps {
  /** Current building name */
  name: string;
  /** Building ID */
  buildingId: number;
  /** Array of existing building names (excluding current building) for duplicate validation */
  existingNames: string[];
  /** Callback function called when saving the edited name */
  onSave: (buildingId: number, newName: string) => Promise<void>;
  /** Whether the building name is currently being updated */
  isUpdating?: boolean;
  /** Visual variant of the component */
  variant?: 'carousel' | 'form';
  /** Custom styles */
  sx?: SxProps<Theme>;
}

export const EditableBuildingName: React.FC<EditableBuildingNameProps> = ({
  name,
  buildingId,
  existingNames,
  onSave,
  isUpdating = false,
  variant = 'carousel',
  sx,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const [error, setError] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update local state when name prop changes (e.g., after successful save)
  useEffect(() => {
    if (!isEditing) {
      setEditedName(name);
    }
  }, [name, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Validates the building name
   * @param value - The name to validate
   * @returns Error message if validation fails, empty string if valid
   */
  const validateName = (value: string): string => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return 'Building name is required';
    }
    if (trimmed.length > 250) {
      return 'Building name must be 250 characters or less';
    }
    // Check for duplicates (existingNames already excludes current building)
    // Comparison is case-insensitive
    const duplicateNames = existingNames.filter(
      n => n.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicateNames.length > 0) {
      return 'Building name must be unique within this order';
    }
    return '';
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card selection
    setIsEditing(true);
    setEditedName(name);
    setError('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedName(name);
    setError('');
  };

  const handleSave = async () => {
    const trimmed = editedName.trim();
    const validationError = validateName(trimmed);

    if (validationError) {
      setError(validationError);
      return;
    }

    // If name hasn't changed, just cancel edit mode
    if (trimmed === name) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      await onSave(buildingId, trimmed);
      setIsEditing(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update building name. Please try again.';
      setError(errorMessage);
      console.error('Failed to update building name:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Delay blur handling to allow click events to fire first
    timeoutRef.current = setTimeout(() => {
      if (isEditing && !isSaving) {
        handleSave();
      }
      timeoutRef.current = null;
    }, 200);
  };

  // Type for editing text field configuration
  interface EditingTextFieldConfig {
    fullWidth?: boolean;
    inputSx?: SxProps<Theme>;
    helperTextSx?: React.CSSProperties | Record<string, unknown>;
    progressSize?: number;
  }

  // Variant-specific configuration
  const variantConfig = {
    form: {
      editingWrapper: {
        position: 'absolute' as const,
        top: 0,
        transform: 'translateY(-50%)',
        backgroundColor: 'background.paper',
        px: 1,
        ml: 1,
        zIndex: 1,
        minWidth: 200,
        maxWidth: 'calc(100% - 2rem)',
        ...sx,
      },
      editingTextField: {
        inputSx: {
          '& .MuiInputBase-root': {
            fontWeight: 500,
          },
        },
        progressSize: 16,
      } as EditingTextFieldConfig,
      readOnlyWrapper: {
        position: 'absolute' as const,
        top: 0,
        transform: 'translateY(-50%)',
        backgroundColor: 'background.paper',
        px: 1,
        ml: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        maxWidth: 'calc(100% - 2rem)',
        ...sx,
      },
      typography: {
        variant: 'subtitle1' as const,
        component: 'h2' as const,
        fontWeight: '500' as const,
        sx: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          flex: 1,
        },
      },
      editIcon: {
        fontSize: '0.875rem',
        opacity: 0,
        transition: 'opacity 0.2s',
        color: 'text.secondary',
        flexShrink: 0,
      },
    },
    carousel: {
      editingWrapper: {
        mb: 1.5,
        position: 'relative' as const,
        ...sx,
      },
      editingTextField: {
        fullWidth: true,
        inputSx: {
          '& .MuiInputBase-root': {
            fontSize: '1rem',
            fontWeight: 700,
          },
        },
        helperTextSx: {
          fontSize: '0.7rem',
          mt: 0.5,
        },
        progressSize: 14,
      } as EditingTextFieldConfig,
      readOnlyWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        mb: 1.5,
        position: 'relative' as const,
        cursor: 'pointer',
        ...sx,
      },
      typography: {
        variant: 'subtitle1' as const,
        sx: {
          fontWeight: 700,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.primary',
          fontSize: '1rem',
        },
      },
      editIcon: {
        fontSize: '0.875rem',
        color: 'text.secondary',
        flexShrink: 0,
        ml: 0.25,
      },
    },
  };

  const config = variantConfig[variant];

  /**
   * Renders the TextField for editing mode
   */
  const renderEditingTextField = () => {
    const { fullWidth = false, inputSx, helperTextSx, progressSize = 16 } = config.editingTextField;

    return (
      <Box sx={config.editingWrapper}>
        <TextField
          inputRef={inputRef}
          value={editedName}
          onChange={e => {
            setEditedName(e.target.value);
            setError('');
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          error={!!error}
          helperText={error}
          disabled={isSaving || isUpdating}
          size="small"
          fullWidth={fullWidth}
          sx={
            [
              inputSx,
              helperTextSx && {
                '& .MuiFormHelperText-root': helperTextSx,
              },
            ].filter(Boolean) as SxProps<Theme>
          }
          InputProps={{
            endAdornment:
              isSaving || isUpdating ? (
                <CircularProgress size={progressSize} sx={{ mr: 1 }} />
              ) : null,
          }}
        />
      </Box>
    );
  };

  /**
   * Renders the read-only display state
   */
  const renderReadOnly = () => {
    return (
      <Box sx={config.readOnlyWrapper} onClick={handleStartEdit}>
        <Typography {...config.typography}>{name}</Typography>
        <EditIcon
          className={variant === 'form' ? 'edit-icon' : undefined}
          aria-label="Edit building name"
          sx={config.editIcon}
        />
      </Box>
    );
  };

  if (isEditing) {
    return renderEditingTextField();
  }

  return renderReadOnly();
};
